import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ui-select__required",
  "aria-hidden": "true"
}
const _hoisted_2 = ["disabled", "aria-expanded", "aria-label"]
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["aria-selected", "onPointerdown", "onMouseenter"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { onDocumentModalEvent } from '@/components/modal-events';
import { computeDropdownPosition, type DropdownPositionStyle } from '@/components/ui/dropdown-position';
import type { SelectOption } from './Select';

const TYPEAHEAD_RESET_MS = 500;


export default /*@__PURE__*/_defineComponent({
  __name: 'Select',
  props: {
    modelValue: {},
    options: {},
    label: { default: '' },
    placeholder: { default: 'Select an option' },
    ariaLabel: { default: '' },
    required: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    variant: { default: 'default' },
    preferPlacement: { default: 'auto' }
  },
  emits: ["update:modelValue", "open", "close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const focusedIndex = ref(-1);
const rootRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const panelStyle = ref<DropdownPositionStyle>({});
let removeModalHideListener: (() => void) | null = null;
let typeaheadBuffer = '';
let typeaheadCycleIndex = 0;
let typeaheadResetTimer: number | null = null;

const instanceId = `ui-select-${Math.random().toString(36).slice(2, 9)}`;
const triggerId = `${instanceId}-trigger`;

const resolvedAriaLabel = computed(() => props.ariaLabel || props.label || 'Select option');
const visibleOptions = computed(() => props.options.filter(option => option.value !== ''));
const isPlaceholder = computed(() => !String(props.modelValue || '').trim());

const selectedLabel = computed(() => {
  const match = props.options.find((option) => option.value === props.modelValue);
  if (match?.value) return match.label;
  if (isPlaceholder.value) return props.placeholder;
  return props.modelValue;
});

function updatePanelPosition(): void {
  if (!triggerRef.value || !panelRef.value) return;
  panelStyle.value = computeDropdownPosition({
    trigger: triggerRef.value,
    panel: panelRef.value,
    placement: props.preferPlacement,
    align: props.compact || props.variant !== 'form' ? 'end' : 'start',
    offset: 4,
    matchTriggerWidth: true,
    zIndex: 2600
  });
}

function forceCloseMenu(): void {
  if (!isOpen.value) return;
  isOpen.value = false;
  focusedIndex.value = -1;
  panelStyle.value = {};
  emit('close');
}

function closeMenu(): void {
  forceCloseMenu();
}

function openMenu(): void {
  if (props.disabled || isOpen.value) return;

  const selectedIndex = visibleOptions.value.findIndex((option) => option.value === props.modelValue);
  focusedIndex.value = selectedIndex >= 0 ? selectedIndex : 0;
  isOpen.value = true;
  emit('open');

  void nextTick(() => {
    updatePanelPosition();
    window.requestAnimationFrame(() => updatePanelPosition());
  });
}

function toggleMenu(): void {
  if (isOpen.value) {
    closeMenu();
    return;
  }
  openMenu();
}

function selectOption(value: string): void {
  forceCloseMenu();
  emit('update:modelValue', value);
}

function isSelected(value: string): boolean {
  return value === props.modelValue;
}

function isFocused(index: unknown): boolean {
  return Number(index) === focusedIndex.value;
}

function focusOption(index: unknown): void {
  focusedIndex.value = Number(index);
}

function getOptionSearchText(option: SelectOption): string {
  return `${option.label} ${option.value.replace(/[-_/]/g, ' ')}`.toLowerCase();
}

function getTypeaheadMatches(buffer: string): SelectOption[] {
  const query = buffer.trim().toLowerCase();
  if (!query) return [];
  return visibleOptions.value.filter((option) => getOptionSearchText(option).startsWith(query));
}

function resetTypeaheadState(): void {
  typeaheadBuffer = '';
  typeaheadCycleIndex = 0;
  if (typeaheadResetTimer !== null) {
    window.clearTimeout(typeaheadResetTimer);
    typeaheadResetTimer = null;
  }
}

function scheduleTypeaheadReset(): void {
  if (typeaheadResetTimer !== null) {
    window.clearTimeout(typeaheadResetTimer);
  }
  typeaheadResetTimer = window.setTimeout(() => {
    resetTypeaheadState();
  }, TYPEAHEAD_RESET_MS);
}

function applyTypeaheadSelection(option: SelectOption): void {
  emit('update:modelValue', option.value);
  if (isOpen.value) {
    const visibleIndex = visibleOptions.value.findIndex((candidate) => candidate.value === option.value);
    if (visibleIndex >= 0) focusedIndex.value = visibleIndex;
  }
}

function handleTypeaheadKey(event: KeyboardEvent): void {
  const char = event.key;
  if (char.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

  event.preventDefault();
  event.stopPropagation();

  const isCycleKey = typeaheadBuffer.length === 1
    && char.toLowerCase() === typeaheadBuffer.toLowerCase();

  if (isCycleKey) {
    typeaheadCycleIndex += 1;
  } else {
    typeaheadBuffer += char;
    typeaheadCycleIndex = 0;
  }

  let matches = getTypeaheadMatches(typeaheadBuffer);
  if (!matches.length && !isCycleKey) {
    typeaheadBuffer = char;
    typeaheadCycleIndex = 0;
    matches = getTypeaheadMatches(typeaheadBuffer);
  }

  if (!matches.length) {
    typeaheadBuffer = '';
    typeaheadCycleIndex = 0;
    scheduleTypeaheadReset();
    return;
  }

  const match = matches[typeaheadCycleIndex % matches.length];
  if (match) applyTypeaheadSelection(match);
  scheduleTypeaheadReset();
}

function moveFocus(direction: 1 | -1): void {
  if (!visibleOptions.value.length) return;
  if (focusedIndex.value < 0) {
    focusedIndex.value = direction > 0 ? 0 : visibleOptions.value.length - 1;
    return;
  }
  focusedIndex.value = (focusedIndex.value + direction + visibleOptions.value.length) % visibleOptions.value.length;
}

function handleDocumentPointerDown(event: PointerEvent): void {
  const target = event.target as Node | null;
  if (!target) return;
  if (rootRef.value?.contains(target) || panelRef.value?.contains(target)) return;
  closeMenu();
}

function handleDocumentKeydown(event: KeyboardEvent): void {
  if (!rootRef.value?.contains(document.activeElement)) return;

  if (!props.disabled && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
    handleTypeaheadKey(event);
    return;
  }

  if (!isOpen.value) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu();
    }
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    closeMenu();
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveFocus(1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveFocus(-1);
    return;
  }

  if (event.key === 'Enter' && focusedIndex.value >= 0) {
    event.preventDefault();
    const option = visibleOptions.value[focusedIndex.value];
    if (option) selectOption(option.value);
  }
}

function handleViewportChange(): void {
  if (isOpen.value) updatePanelPosition();
}

watch(() => props.modelValue, (value, previousValue) => {
  if (value !== previousValue) forceCloseMenu();
});

watch(() => props.disabled, (isDisabled) => {
  if (isDisabled) closeMenu();
});

watch(isOpen, (open) => {
  if (open) {
    window.addEventListener('resize', handleViewportChange, true);
    window.addEventListener('scroll', handleViewportChange, true);
    return;
  }
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  document.addEventListener('keydown', handleDocumentKeydown);
  removeModalHideListener = onDocumentModalEvent('hide.bs.modal', closeMenu);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  document.removeEventListener('keydown', handleDocumentKeydown);
  removeModalHideListener?.();
  resetTypeaheadState();
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

__expose({
  forceCloseMenu,
  closeMenu: forceCloseMenu
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: _normalizeClass(["ui-select", {
    'ui-select--compact': __props.compact,
    'ui-select--form': __props.variant === 'form',
    'is-open': isOpen.value,
    'is-disabled': __props.disabled
  }])
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: triggerId,
          class: "ui-select__label"
        }, [
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, "*"))
            : _createCommentVNode("v-if", true),
          _createTextVNode(_toDisplayString(__props.label), 1 /* TEXT */)
        ]))
      : _createCommentVNode("v-if", true),
    _createElementVNode("button", {
      id: triggerId,
      ref_key: "triggerRef",
      ref: triggerRef,
      type: "button",
      class: "ui-select__trigger",
      disabled: __props.disabled,
      "aria-expanded": isOpen.value ? 'true' : 'false',
      "aria-haspopup": 'listbox',
      "aria-label": resolvedAriaLabel.value,
      onClick: _withModifiers(toggleMenu, ["stop"])
    }, [
      _createElementVNode("span", {
        class: _normalizeClass(["ui-select__value", { 'is-placeholder': isPlaceholder.value }])
      }, _toDisplayString(selectedLabel.value), 3 /* TEXT, CLASS */),
      _cache[0] || (_cache[0] = _createElementVNode("i", {
        class: "material-icons ui-select__chevron",
        "aria-hidden": "true"
      }, "expand_more", -1 /* CACHED */))
    ], 8 /* PROPS */, _hoisted_2),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "panelRef",
            ref: panelRef,
            class: "ui-select__panel ui-select__panel--portal",
            style: _normalizeStyle(panelStyle.value),
            role: "listbox",
            "aria-label": resolvedAriaLabel.value
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleOptions.value, (option, index) => {
              return (_openBlock(), _createElementBlock("button", {
                key: option.value,
                type: "button",
                role: "option",
                class: _normalizeClass(["ui-select__option", { 'is-selected': isSelected(option.value), 'is-focused': isFocused(index) }]),
                "aria-selected": isSelected(option.value) ? 'true' : 'false',
                onPointerdown: _withModifiers(($event: any) => (selectOption(option.value)), ["stop","prevent"]),
                onMouseenter: ($event: any) => (focusOption(index))
              }, _toDisplayString(option.label), 43 /* TEXT, CLASS, PROPS, NEED_HYDRATION */, _hoisted_4))
            }), 128 /* KEYED_FRAGMENT */))
          ], 12 /* STYLE, PROPS */, _hoisted_3))
        : _createCommentVNode("v-if", true)
    ]))
  ], 2 /* CLASS */))
}
}

})